import {useTranslation} from 'react-i18next';
import {UiMarkerActions, UiNoValueActions, useUiContext} from 'src/context/UiContext';
import {Location, ScheduledStop} from 'src/services/api/types/types';
import {ScheduledStops} from '../components/ScheduledStops';
import {fetchScheduledStopsAtLocation} from 'src/services/api/api';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {addDays, format, isValid, parse} from 'date-fns';

interface LocationListItemProps {
    location: Location;
    reasonParams: Array<string>;
    distanceToLocation?: {value: number; unit: string};
}

const baseUrl = process.env.REACT_APP_CLINIC_API_ENDPOINT;

export function LocationListItem({location, reasonParams, distanceToLocation}: LocationListItemProps) {
    const listItemRef = useRef<HTMLDivElement>(null);
    const [intersecting, setIntersecting] = useState(false);
    const {state, dispatch} = useUiContext();
    const {t} = useTranslation();
    const canHover = matchMedia('(hover: hover)').matches;
    const logoUrl = `${baseUrl}/v1/careGivers/${location.careGiver}/logo`;
    const locationDetailsUrl = '/plats';

    const parseDateString = useCallback((value: string) => {
        const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(parsedDate) ? parsedDate : null;
    }, []);
    const [urlSearchParams] = useSearchParams();
    const today = format(new Date(), 'yyyy-MM-dd');
    const defaultTo = format(addDays(new Date(), 30), 'yyyy-MM-dd');
    const from = parseDateString(urlSearchParams.get('from') ?? today) ?? new Date();
    const to =
        parseDateString(urlSearchParams.get('to') ?? defaultTo) ?? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    const [scheduledStops, setScheduledStops] = useState<Array<ScheduledStop>>([]);
    const [loadedStops, setLoadedStops] = useState(false);

    const getBookingLabel = ({hasDropin, hasTimebook}: Location) => {
        if (hasDropin && hasTimebook) {
            return t('clinic_popup.booking_options.both');
        }
        if (hasDropin) {
            return t('clinic_popup.booking_options.dropin');
        }
        if (hasTimebook) {
            return t('clinic_popup.booking_options.timebook');
        }
    };

    useEffect(() => {
        const loadSchedule = async () => {
            if (loadedStops) return;
            const stops = await fetchScheduledStopsAtLocation(location.id, from, to);
            setScheduledStops(stops.slice(0, 3));
            setLoadedStops(true);
        };

        if (intersecting) {
            loadSchedule();
        }
    }, [location, intersecting, loadedStops]);

    useEffect(() => {
        if (listItemRef.current === null) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                setIntersecting(entries[0].isIntersecting);
            },
            {
                rootMargin: '0px',
                threshold: 0.5
            }
        );

        observer.observe(listItemRef.current);

        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={listItemRef}
            role='button'
            className={`map-list-item${state.stickyClinicId?.type === 'LOCATION' && state.stickyClinicId.id === location.id ? ' map-list-item--sticky' : ''}`}
            onMouseEnter={() => {
                if (canHover) {
                    dispatch({type: UiMarkerActions.SET_HOVERED_CLINIC_ID, value: {type: 'LOCATION', id: location.id}});
                }
            }}
            onMouseLeave={() => {
                if (canHover) {
                    dispatch({type: UiMarkerActions.SET_HOVERED_CLINIC_ID, value: null});
                }
            }}
            onClick={() => {
                if (canHover || state.smallDevice) {
                    return;
                }

                if (state.stickyClinicId?.type === 'LOCATION' && state.stickyClinicId.id === location.id) {
                    dispatch({type: UiNoValueActions.CLEAR_STICKY_CLINIC_ID});
                } else {
                    dispatch({type: UiMarkerActions.SET_STICKY_CLINIC_ID, value: {type: 'LOCATION', id: location.id}});
                }
            }}
            tabIndex={-1}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    if (state.stickyClinicId?.type === 'LOCATION' && state.stickyClinicId.id === location.id) {
                        dispatch({type: UiNoValueActions.CLEAR_STICKY_CLINIC_ID});
                    } else {
                        dispatch({
                            type: UiMarkerActions.SET_STICKY_CLINIC_ID,
                            value: {type: 'LOCATION', id: location.id}
                        });
                    }
                }
            }}
        >
            {logoUrl && (
                <div className='map-list-item__logo'>
                    <img src={logoUrl} alt={location.name} aria-hidden={true} />
                </div>
            )}
            <p className='map-list-item__clinic-name'>
                <b>{location.name}</b>
            </p>
            <div className='map-list-item__content'>
                <div className='map-list-item__content__text-container'>
                    <p>
                        {distanceToLocation && (
                            <>
                                <span className='screen-reader-only'>
                                    {t('distance_to_clinic.arialabel', distanceToLocation)}
                                </span>
                                <span aria-hidden>{`${distanceToLocation.value} ${distanceToLocation.unit} •`}</span>
                            </>
                        )}
                        <span aria-hidden>{distanceToLocation && <>&nbsp;</>}</span>
                        {location.address && `${location.address}, ${location.city}`}
                    </p>
                    <p>{getBookingLabel(location)}</p>
                </div>

                <ScheduledStops scheduledStops={scheduledStops} includeInfo={false} />
            </div>

            <p className='map-list-item__link'>
                <a
                    aria-label={
                        location.externalBookingUrl && location.externalBookingUrlType === 'caregiver'
                            ? t('list_clinics_to_caregiver.arialabel')
                            : t('location_popup.button_location.text')
                    }
                    href={
                        location.externalBookingUrl
                            ? location.externalBookingUrl
                            : `${locationDetailsUrl}/${location.id}`
                    }
                    className='btn btn--primary'
                    target='_blank'
                    rel='noreferrer'
                >
                    {location.externalBookingUrl && location.externalBookingUrlType === 'caregiver'
                        ? t('list_clinics_to_caregiver.text')
                        : t('location_popup.button_location.text')}
                </a>
            </p>
        </div>
    );
}
